import { AfterViewChecked, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { unsubscribeAll } from 'src/app/common/app.helpers';
import { FilesValidationHandler } from 'src/app/components/files/files-validation.handler';
import { OnFileUploaded } from 'src/app/interfaces/file.interface';
import { FieldAttribute } from 'src/app/interfaces/form-and-field.interface';
import FieldAllowedValueClasses from '../../../field-allowed-value-classes.values';
import { FileUploadDelegate } from '../file-upload.delegate';

@Component({
  selector: 'app-file-form-field',
  templateUrl: './file-form-field.component.html',
  styleUrls: ['./file-form-field.component.scss'],
  providers: [FileUploadDelegate],
})
export class FileFormFieldComponent implements OnInit, AfterViewChecked, OnDestroy {
  @Input({ required: true }) public inputContainerStyle = '';
  @Input({ required: true }) public group: UntypedFormGroup;
  @Input({ required: true }) public attribute: FieldAttribute;
  @Input({ required: true }) public valid = true;

  @Output() public fileUpdated: EventEmitter<string> = new EventEmitter<string>();
  @Output() public onError: EventEmitter<boolean> = new EventEmitter<boolean>();

  public fileName: string;
  public acceptedFilesExtensions = '';

  private fileInput: HTMLInputElement;
  private control: AbstractControl;
  private subscriptions$: Subscription[] = [];

  private filesValidationHandler = inject(FilesValidationHandler);
  private fileUploadDelegate = inject(FileUploadDelegate);

  public ngOnInit(): void {
    this.subscribeOnFileUploaded();
    this.subscribeOnFolderCreated();
    this.subscribeOnFileUploadedError();
    this.initInputFileField();
  }

  public ngAfterViewChecked(): void {
    this.fileInput = document.getElementById(this.attribute.backendAttribute.name) as HTMLInputElement;
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  /**
   * @description Called on input change event. Calls uploadFile().
   * @param { Event } event Input change event content.
   */
  public onFileChanged(event: Event): void {
    this.fileUploadDelegate.uploadFile({ event, attr: this.attribute, enableCrop: true });
    this.fileInput.value = '';
  }

  /**
   * @description Cleares control value.
   */
  public deleteAssociation(): void {
    this.control.setValue('');
    this.control.enable({ onlySelf: true });
    this.fileName = '';
  }

  /**
   * @description Checks if the current field value_class_front value is type
   *              of FieldAllowedTypes.PHOTO.
   */
  public isNotPhoto(): boolean {
    return this.attribute.backendAttribute.value_class_front !== FieldAllowedValueClasses.PHOTO;
  }

  /**
   * @description Inits the control and sets up the acceptedFilesExtensions value.
   */
  private initInputFileField(): void {
    this.control = this.group.get(this.attribute.backendAttribute.name);

    this.acceptedFilesExtensions = this.filesValidationHandler.getAcceptedFilesExtensions(
      this.attribute.backendAttribute.value_class_front,
    );
  }

  /**
   * @description Subscribes on onFileChanged event if multiple property value is falsy
   *              and emits link to parent component in case passed conditions.
   */
  private subscribeOnFileUploaded(): void {
    if (!this.attribute.multiple) {
      this.subscriptions$.push(
        this.fileUploadDelegate.onFileChanged.subscribe((response: OnFileUploaded) => {
          this.processFileManagerChanges(response);
        }),
      );
    }
  }

  /**
   * @description Subscribes on onFolderCreated event if multiple property value is truthy
   *              and emits link to parent component in case passed conditions.
   */
  private subscribeOnFolderCreated(): void {
    if (this.attribute.multiple) {
      this.subscriptions$.push(
        this.fileUploadDelegate.onFolderCreated.subscribe((response: OnFileUploaded) => {
          this.processFileManagerChanges(response);
        }),
      );
    }
  }

  /**
   * @description Subscribes on onErrorOccurred event and emits error to parent component
   *              in case error.
   */
  private subscribeOnFileUploadedError(): void {
    this.subscriptions$.push(
      this.fileUploadDelegate.onErrorOccurred.subscribe((response: OnFileUploaded) => {
        if (response.controlName === this.attribute.backendAttribute.name) {
          this.fileInput.value = '';
          this.fileName = response.fileName;
          this.onError.emit(true);
        }
      }),
    );
  }

  /**
   * @description Shared method for processing file manager changes.
   *      1. If the response contains not empty link and the response's controlName is
   *         current field name, updates the component.
   *      2. Otherwise does nothing.
   * @param { OnFileUploaded } response
   */
  private processFileManagerChanges(response: OnFileUploaded): void {
    if (response.controlName === this.attribute.backendAttribute.name && response.link) {
      this.fileName = response.fileName;
      this.control.setValue(response.link);
      this.control.setErrors(null);
      this.fileUpdated.emit(response.link);
    }
  }
}
